import React, { useEffect, useState } from "react";
import { Building2, Plus, Pencil, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, Spinner, PageHeader, StatusPill, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = {
  name: "", contact_person: "", phone: "", email: "", start_date: "", billing_frequency: "Monthly",
  payment_terms: "Net 30", status: "Active", notes: "", markup_pct: 15, cost_visibility: true, opening_balance: 0,
  portal_modules: ["orders", "menu", "inventory", "billing", "wastage", "reports"], portal_show_recipes: true,
};
const PORTAL_MODULES = [
  { key: "orders", label: "Orders" }, { key: "menu", label: "Menu & Recipes" }, { key: "inventory", label: "Inventory" },
  { key: "billing", label: "Client Billing" }, { key: "wastage", label: "Wastage" }, { key: "reports", label: "Reports" },
];

export default function Clients() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delItem, setDelItem] = useState(null);

  const del = async () => {
    try {
      await api.delete(`/clients/${delItem.id}`);
      toast.success("Client deleted");
      setDelItem(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setDelItem(null); }
  };

  const load = () => api.get("/clients").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c, portal_modules: c.portal_modules || EMPTY.portal_modules, portal_show_recipes: c.portal_show_recipes !== false }); setEditId(c.id); setOpen(true); };
  const toggleModule = (k) => setForm((f) => ({ ...f, portal_modules: f.portal_modules.includes(k) ? f.portal_modules.filter((m) => m !== k) : [...f.portal_modules, k] }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, markup_pct: form.markup_pct === "" ? null : Number(form.markup_pct), opening_balance: Number(form.opening_balance) };
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success(editId ? "Client updated" : "Client created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!list) return <Spinner />;

  return (
    <div>
      <PageHeader title="Clients" subtitle="Managed cloud-kitchen clients and their billing terms">
        <Button onClick={openNew} data-testid="add-client-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
          <Plus className="h-4 w-4" /> New Client
        </Button>
      </PageHeader>

      {list.length === 0 ? (
        <Empty icon={Building2} title="No clients yet" hint="Add your 5 clients to begin." />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="card-surface p-5" data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-display font-semibold text-white truncate">{c.name}</h3>
                  <p className="text-xs text-muted-foreground">{c.contact_person} · {c.phone}</p>
                </div>
                <StatusPill status={c.status} />
              </div>
              <div className="grid grid-cols-2 gap-y-2 gap-x-3 mt-4 text-sm">
                <Info label="Billing" value={c.billing_frequency} />
                <Info label="Terms" value={c.payment_terms} />
                <Info label="Markup" value={c.markup_pct != null ? `${c.markup_pct}%` : "Default"} />
                <Info label="Cost visible" value={c.cost_visibility ? "Yes" : "No"} />
              </div>
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Current Balance</p>
                  <p className="font-mono font-bold text-amber-300" data-testid={`client-balance-${c.id}`}>{money(c.balance?.current_balance)}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`} className="gap-1.5">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button variant="outline" size="icon" onClick={() => setDelItem(c)} data-testid={`delete-client-${c.id}`} className="h-9 w-9 ml-2">
                  <Trash2 className="h-4 w-4 text-rose-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card border-border">
          <DialogHeader><DialogTitle>{editId ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldW label="Client name" full><Input data-testid="client-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></FieldW>
            <FieldW label="Contact person"><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></FieldW>
            <FieldW label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></FieldW>
            <FieldW label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></FieldW>
            <FieldW label="Start date"><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></FieldW>
            <FieldW label="Billing frequency">
              <SelectBox value={form.billing_frequency} onChange={(v) => set("billing_frequency", v)} options={["Daily", "Weekly", "Monthly"]} />
            </FieldW>
            <FieldW label="Payment terms">
              <SelectBox value={form.payment_terms} onChange={(v) => set("payment_terms", v)} options={["Prepaid", "Net 7", "Net 15", "Net 30"]} />
            </FieldW>
            <FieldW label="Client markup %"><Input type="number" data-testid="client-markup" value={form.markup_pct ?? ""} onChange={(e) => set("markup_pct", e.target.value)} placeholder="Default" /></FieldW>
            <FieldW label="Opening balance (AED)"><Input type="number" value={form.opening_balance} onChange={(e) => set("opening_balance", e.target.value)} /></FieldW>
            <FieldW label="Status">
              <SelectBox value={form.status} onChange={(v) => set("status", v)} options={["Active", "Inactive"]} />
            </FieldW>
            <div className="sm:col-span-2 flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label>Client cost visibility</Label>
                <p className="text-xs text-muted-foreground">Show client-facing recipe cost in their portal</p>
              </div>
              <Switch checked={form.cost_visibility} onCheckedChange={(v) => set("cost_visibility", v)} data-testid="client-cost-visibility" />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label>Show recipes in portal</Label>
                <p className="text-xs text-muted-foreground">Let this client view the ingredient breakdown of their menu items</p>
              </div>
              <Switch checked={form.portal_show_recipes} onCheckedChange={(v) => set("portal_show_recipes", v)} data-testid="client-show-recipes" />
            </div>
            <div className="sm:col-span-2 rounded-lg border border-border p-3">
              <Label>Portal modules</Label>
              <p className="text-xs text-muted-foreground mb-2">Choose which sections this client can see in their portal</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PORTAL_MODULES.map((m) => (
                  <label key={m.key} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid={`portal-mod-${m.key}`}>
                    <input type="checkbox" checked={form.portal_modules.includes(m.key)} onChange={() => toggleModule(m.key)} className="h-4 w-4" />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
            <FieldW label="Notes" full><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></FieldW>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name} data-testid="save-client-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delItem?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the client and is only allowed when no brands, orders or billing records are linked.
              If any exist, set the client to Inactive instead to preserve history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="confirm-delete-client" className="bg-rose-600 hover:bg-rose-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{label}</p>
      <p className="text-sm text-slate-200">{value}</p>
    </div>
  );
}
export function FieldW({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
export function SelectBox({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
            {typeof o === "string" ? o : o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
